import { InfoOutlined, MoreHoriz } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { type FC, useCallback, useState } from 'react'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { useDeleteLabel } from 'src/features/labelMaster/api/useDeleteLabel'
import { getSourceLabelId, setSourceLabelId } from './copyLabelAreas'
import { PasteAreaModal } from './PasteAreaModal'

type Props = {
  id: string
  hasAreas: boolean
}

export const LabelActionMenu: FC<Props> = ({ id, hasAreas }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const deleteMutation = useDeleteLabel()
  const { showInfoSnackbar } = useSnackbar()

  const sourceLabelId = getSourceLabelId()
  const canCopyAreas = hasAreas

  // コピー元が指定されていて、コピー元と自身が一致しない場合はペースト可能
  const canPasteAreas = !!sourceLabelId && id !== sourceLabelId

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleCopyAreas = useCallback(() => {
    setSourceLabelId(id)
    showInfoSnackbar('検査箇所をコピーしました')
    handleClose()
  }, [id, showInfoSnackbar])

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(id)
    setDeleteDialogOpen(false)
    handleClose()
  }, [id, deleteMutation])

  return (
    <>
      <IconButton size="small" onClick={handleMenuClick}>
        <MoreHoriz />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={e => e.stopPropagation()}
      >
        {canCopyAreas && (
          <MenuItem onClick={handleCopyAreas}>
            <Typography
              variant="body2"
              color="text.primary"
              fontWeight="500"
              textAlign="left"
            >
              検査箇所のコピー
            </Typography>
          </MenuItem>
        )}
        {canPasteAreas && (
          <PasteAreaModal
            sourceLabelId={sourceLabelId}
            id={id}
            hasAreas={hasAreas}
          />
        )}
        {/* コピー&ペーストのMenuItemが表示されている時、区別するためにDividerを表示する */}
        {(canCopyAreas || canPasteAreas) && <Divider />}
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true)
            handleClose()
          }}
        >
          <Typography
            variant="body2"
            color="error"
            fontWeight="500"
            textAlign="left"
          >
            削除
          </Typography>
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        onClick={e => e.stopPropagation()}
      >
        <DialogContent sx={{ padding: 4 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <InfoOutlined sx={{ color: 'warning.main' }} />
            <Typography fontWeight="bold" variant="h6" color="text.primary">
              ラベルを削除しますか？
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            size="medium"
            color="secondary"
            onClick={() => setDeleteDialogOpen(false)}
          >
            キャンセル
          </Button>
          <Button
            variant="text"
            color="error"
            size="medium"
            onClick={handleDelete}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
